import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Modal, Form, Row, Col, InputGroup } from 'react-bootstrap';
import { fetchEmailManagementRequest, deleteEmailManagementRequest } from '../../store/actions/emailManagementActions';
import { FiMail, FiRefreshCw, FiSearch, FiEye, FiEdit, FiTrash2, FiPlus } from 'react-icons/fi';
import { getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import PartnerCompanyAutocomplete from '../../components/Common/PartnerCompanyAutocomplete';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminEmail.css';

const EMPTY_EMAIL_FILTERS = {
    email: '',
    company_name: '',
};

const AdminEmail = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { emailManagement, delete: deleteState } = useSelector((state) => state.emailManagement);
    const {
        filters,
        setFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        setItemsPerPage,
        companyPartnerSearch,
        setCompanyPartnerSearch,
        clearSearchState,
    } = usePersistedSearchState('email-management', EMPTY_EMAIL_FILTERS, {
        withAppliedFilters: false,
        withCompanyPartnerSearch: true,
        mutableItemsPerPage: true,
    });
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnersError, setPartnersError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedEmailManagementId, setSelectedEmailManagementId] = useState(null);
    const [deleteFeedback, setDeleteFeedback] = useState(null);
    const [partners, setPartners] = useState([]);
    const [meta, setMeta] = useState({
        total: 0,
        skip: 0,
        limit: 10,
        page: 1,
        total_pages: 1,
    });

    const partnerCompanyOptions = useMemo(() => {
        const names = partners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [partners]);

    const fetchData = () => {
        const companyName = (filters.company_name || companyPartnerSearch).trim();
        const skip = (currentPage - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
            ...Object.fromEntries(
                Object.entries({
                    email: filters.email,
                    company_name: companyName,
                }).filter(([_, value]) => value && value.trim() !== '')
            ),
        };
        dispatch(fetchEmailManagementRequest(params));
    };

    const handleApplyFilters = () => {
        const companyName = (filters.company_name || companyPartnerSearch).trim();
        setFilters((prev) => ({ ...prev, company_name: companyName }));
        setCompanyPartnerSearch(companyName);
        setCurrentPage(1);
    };

    const handleFilterKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        clearSearchState();
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage]);

    useEffect(() => {
        setCurrentPage(1);
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.email, filters.company_name]);

    const fetchPartners = async () => {
        try {
            setPartnersLoading(true);
            setPartnersError(null);
            const response = await getAllPartners();
            let partnersList = [];
            if (response?.users && Array.isArray(response.users)) {
                partnersList = response.users;
            } else if (Array.isArray(response)) {
                partnersList = response;
            } else if (response?.partners && Array.isArray(response.partners)) {
                partnersList = response.partners;
            } else if (response?.data && Array.isArray(response.data)) {
                partnersList = response.data;
            }
            setPartners(partnersList);
        } catch (error) {
            console.error('Erreur lors du chargement des partenaires:', error);
            setPartners([]);
            setPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
        } finally {
            setPartnersLoading(false);
        }
    };

    useEffect(() => {
        fetchPartners();
    }, []);

    useEffect(() => {
        if (deleteState.success) {
            setShowDeleteModal(false);
            setSelectedEmailManagementId(null);
            setDeleteFeedback({
                variant: 'success',
                message: deleteState.message
                    || 'Email management deleted successfully.',
            });
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteState.success, deleteState.message]);

    useEffect(() => {
        if (deleteState.error) {
            setDeleteFeedback({
                variant: 'danger',
                message: deleteState.error,
            });
        }
    }, [deleteState.error]);

    const handleDetails = (emailManagementId) => {
        navigate(`/admin/email/${emailManagementId}`);
    };

    const handleEdit = (emailManagementId) => {
        navigate(`/admin/email/edit/${emailManagementId}`);
    };

    const handleDelete = (emailManagementId) => {
        setDeleteFeedback(null);
        setSelectedEmailManagementId(emailManagementId);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (selectedEmailManagementId) {
            dispatch(deleteEmailManagementRequest(selectedEmailManagementId));
        }
    };

    const handleRefresh = () => {
        fetchData();
    };

    const handleCreate = () => {
        navigate('/admin/email/create');
    };

    // Ensure records is an array
    const records = emailManagement.data?.data?.records || emailManagement.data?.records || emailManagement.data?.data || emailManagement.data || [];
    const recordsArray = Array.isArray(records) ? records : [];

    // Extraire les métadonnées de pagination
    useEffect(() => {
        const d = emailManagement.data;
        let paginationMeta = {
            total: 0,
            skip: 0,
            limit: itemsPerPage,
            page: currentPage,
            total_pages: 1,
        };

        if (d?.meta) {
            paginationMeta = {
                total: d.meta.total || 0,
                skip: d.meta.skip || 0,
                limit: d.meta.limit || itemsPerPage,
                page: d.meta.page || currentPage,
                total_pages: d.meta.total_pages || Math.ceil((d.meta.total || 0) / (d.meta.limit || itemsPerPage)),
            };
        } else if (d?.total !== undefined) {
            paginationMeta = {
                total: d.total || 0,
                skip: d.skip || 0,
                limit: d.limit || itemsPerPage,
                page: d.page || currentPage,
                total_pages: d.total_pages || Math.ceil((d.total || 0) / (d.limit || itemsPerPage)),
            };
        }
        setMeta(paginationMeta);
    }, [emailManagement.data, itemsPerPage, currentPage]);

    // Utiliser directement les records car la pagination et le filtrage sont faits côté serveur
    const currentRecords = recordsArray;
    const totalPages = meta.total_pages || 1;
    const totalItems = meta.total || recordsArray.length;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = indexOfFirstItem + recordsArray.length;

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPaginationItems = () => {
        const items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    return (
        <div className="dashboard-page admin-email-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiMail className="me-2" />
                                Gestion des Emails
                            </h2>
                            <p className="text-muted mb-0">Gérez tous les emails et leurs processus associés</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="outline-primary" onClick={handleRefresh} disabled={emailManagement.loading}>
                                <FiRefreshCw className={emailManagement.loading ? 'spinning' : ''} /> Actualiser
                            </Button>
                            <Button variant="primary" onClick={handleCreate}>
                                <FiPlus /> Nouvel Email Management
                            </Button>
                        </div>
                    </div>

                    {emailManagement.error && (
                        <Alert variant="danger" className="mt-3">
                            {emailManagement.error}
                        </Alert>
                    )}
                    {deleteFeedback?.message && (
                        <Alert
                            variant={deleteFeedback.variant}
                            className="mt-3"
                            dismissible
                            onClose={() => setDeleteFeedback(null)}
                        >
                            {deleteFeedback.message}
                        </Alert>
                    )}

                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleClearFilters}
                                >
                                    Réinitialiser
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleApplyFilters}
                                >
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Email</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FiSearch />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Filtrer par email..."
                                                value={filters.email}
                                                onChange={(e) => setFilters((prev) => ({ ...prev, email: e.target.value }))}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleApplyFilters();
                                                    }
                                                }}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Label>Nom de société</Form.Label>
                                    <PartnerCompanyAutocomplete
                                        value={filters.company_name}
                                        onChange={(companyName) => setFilters((prev) => ({ ...prev, company_name: companyName }))}
                                        onInputChange={setCompanyPartnerSearch}
                                        options={partnerCompanyOptions}
                                        loading={partnersLoading}
                                        error={partnersError}
                                        onKeyDown={handleFilterKeyPress}
                                        placeholder="Rechercher un partenaire…"
                                    />
                                </Col>
                            </Row>
                            <div className="d-flex align-items-center gap-3">
                                <span className="text-muted">Éléments par page :</span>
                                <Form.Select
                                    size="sm"
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    style={{ width: 'auto' }}
                                >
                                    {[10, 20, 50, 100].map((value) => (
                                        <option key={value} value={value}>
                                            {value}
                                        </option>
                                    ))}
                                </Form.Select>
                                <span className="text-muted ms-auto">
                                    Page {currentPage} sur {totalPages} — Total : {totalItems}
                                </span>
                            </div>
                        </Card.Body>
                    </Card>

                    <div className="table-content">
                        {emailManagement.loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-3">Chargement des emails...</p>
                            </div>
                        ) : recordsArray.length === 0 ? (
                            <div className="text-center py-5">
                                <FiMail size={48} className="text-muted mb-3" />
                                <p className="text-muted">
                                    {(filters.email || filters.company_name) ? 'Aucun email trouvé avec ces filtres' : 'Aucun email disponible'}
                                </p>
                            </div>
                        ) : (
                            <div className="table-responsive table-scroll">
                                <Table hover striped className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Prénom</th>
                                            <th>Nom</th>
                                            <th>Email</th>
                                            <th>Téléphone</th>
                                            <th>Entreprise</th>
                                            <th>Processus Email</th>
                                            <th>Email(s)</th>
                                            <th>Date de création</th>
                                            <th>Dernière modification</th>
                                            <th className="text-end col-actions">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRecords.map((record, index) => {
                                            const emailMgmt = record.email_management || {};
                                            const user = record.user || {};
                                            return (
                                                <tr key={emailMgmt.email_management_id || index}>
                                                    <td>{user.first_name || 'N/A'}</td>
                                                    <td>{user.last_name || 'N/A'}</td>
                                                    <td>{user.email || 'N/A'}</td>
                                                    <td>{user.phone_number || 'N/A'}</td>
                                                    <td>{user.company_name || 'N/A'}</td>
                                                    <td>
                                                        <span className="badge bg-info">
                                                            {emailMgmt.email_process || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {emailMgmt.email_list && Array.isArray(emailMgmt.email_list) && emailMgmt.email_list.length > 0 ? (
                                                            <span className="badge bg-success">
                                                                {emailMgmt.email_list.length} Email(s)
                                                            </span>
                                                        ) : (
                                                            <span className="badge bg-secondary">Aucun</span>
                                                        )}
                                                    </td>
                                                    <td>{formatDate(emailMgmt.created_at)}</td>
                                                    <td>{formatDate(emailMgmt.updated_at)}</td>
                                                    <td className="text-end col-actions">
                                                        <div className="d-flex gap-2 justify-content-end flex-nowrap">
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                onClick={() => handleDetails(emailMgmt.email_management_id)}
                                                                title="Détails"
                                                                aria-label="Détails"
                                                            >
                                                                <FiEye />
                                                            </Button>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleEdit(emailMgmt.email_management_id)}
                                                                title="Modifier"
                                                                aria-label="Modifier"
                                                            >
                                                                <FiEdit />
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleDelete(emailMgmt.email_management_id)}
                                                                title="Supprimer"
                                                                aria-label="Supprimer"
                                                            >
                                                                <FiTrash2 />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {!emailManagement.loading && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div className="text-muted">
                                Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, totalItems)} sur {totalItems} email(s)
                            </div>
                            <Pagination>
                                <Pagination.Prev
                                    disabled={currentPage === 1}
                                    onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                                />
                                {renderPaginationItems()}
                                <Pagination.Next
                                    disabled={currentPage === totalPages}
                                    onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                                />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmer la suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteState.error && (
                        <Alert variant="danger" className="mb-3">
                            {deleteState.error}
                        </Alert>
                    )}
                    Êtes-vous sûr de vouloir supprimer cet email management ? Cette action est irréversible.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteState.loading}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={confirmDelete} disabled={deleteState.loading}>
                        {deleteState.loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            'Supprimer'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminEmail;
